
import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { BarChart2, Plus, Trash2, X, Play, StopCircle, RefreshCw } from 'lucide-react';

interface Poll {
    id: number;
    question: string;
    status: 'OPEN' | 'CLOSED';
    total_votes: number;
    options: { id: number, label: string, votes: number, voters?: string[] }[];
    created_at: string;
}

export const PollAdmin: React.FC = () => {
    const { user } = useAuth();
    const [polls, setPolls] = useState<Poll[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    
    // New Poll Form
    const [question, setQuestion] = useState('');
    const [options, setOptions] = useState(['Ja', 'Nein', '']);
    const [selectedEventId, setSelectedEventId] = useState<string>('');

    if (!user || (!user.roles.includes(UserRole.ADMIN) && !user.roles.includes(UserRole.WEBADMIN))) {
        return <Navigate to="/" />;
    }

    const loadData = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const [pollRes, eventRes] = await Promise.all([
                apiRequest<Poll[]>(`polls.php?admin=true&userId=${user.id}`),
                apiRequest<any[]>('events.php')
            ]);
            setPolls(Array.isArray(pollRes) ? pollRes : []);
            setEvents(Array.isArray(eventRes) ? eventRes.sort((a,b) => b.roundNumber - a.roundNumber) : []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadData(); }, []);

    const handleCreate = async () => {
        if (!question.trim()) return;
        const validOptions = options.filter(o => o.trim() !== '');
        if (validOptions.length < 2) {
            alert("Mindestens 2 Optionen erforderlich.");
            return;
        }

        try {
            await apiRequest('polls.php', 'POST', {
                userId: user.id,
                question,
                options: validOptions,
                eventId: selectedEventId || null
            });
            setIsCreating(false);
            setQuestion('');
            setOptions(['Ja', 'Nein', '']);
            setSelectedEventId('');
            loadData();
        } catch(e: any) { alert(e.message); }
    };

    const handleStatus = async (id: number, status: 'OPEN' | 'CLOSED') => {
        try {
            await apiRequest('polls.php', 'PUT', { id, status });
            loadData();
        } catch(e: any) { alert(e.message); }
    };

    const handleDelete = async (id: number) => {
        if (!confirm("Umfrage löschen?")) return;
        try {
            await apiRequest(`polls.php?id=${id}`, 'DELETE');
            loadData();
        } catch(e: any) { alert(e.message); }
    };

    const handleOptionChange = (idx: number, val: string) => {
        const newOpts = [...options];
        newOpts[idx] = val;
        setOptions(newOpts);
    };

    const addOptionSlot = () => setOptions([...options, '']);

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3">
                    <BarChart2 className="text-f1-red"/> Community-Umfragen
                    </h1>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" icon={RefreshCw} onClick={loadData}>Refresh</Button>
                    <Button icon={Plus} onClick={() => setIsCreating(true)}>Neue Umfrage</Button>
                </div>
            </div>

            {isCreating && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg bg-f1-dark border-gray-600">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold uppercase text-white">Neue Umfrage erstellen</h3>
                            <button onClick={() => setIsCreating(false)}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        
                        <div className="space-y-4">
                            <Input label="Frage" value={question} onChange={e => setQuestion(e.target.value)} placeholder="Z.B. Soll es regnen?"/>
                            
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Verknüpftes Rennen (Optional)</label>
                                <select 
                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm"
                                    value={selectedEventId}
                                    onChange={e => setSelectedEventId(e.target.value)}
                                >
                                    <option value="">Kein Rennen</option>
                                    {events.map(ev => (
                                        <option key={ev.id} value={ev.id}>R{ev.roundNumber}: {ev.trackLocation}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Antwortmöglichkeiten</label>
                                <div className="space-y-2">
                                    {options.map((opt, idx) => (
                                        <input 
                                            key={idx}
                                            className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm"
                                            value={opt}
                                            onChange={e => handleOptionChange(idx, e.target.value)}
                                            placeholder={`Option ${idx+1}`}
                                        />
                                    ))}
                                    <button onClick={addOptionSlot} className="text-xs text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1">
                                        <Plus size={12}/> Weitere Option
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-700">
                            <Button variant="ghost" onClick={() => setIsCreating(false)}>Abbrechen</Button>
                            <Button onClick={handleCreate} disabled={!question}>Erstellen & Posten</Button>
                        </div>
                    </Card>
                </div>
            )}

            <div className="grid gap-4">
                {polls.map(poll => (
                    <Card key={poll.id} className={`border-l-4 ${poll.status === 'OPEN' ? 'border-green-500' : 'border-gray-600'}`}>
                        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <Badge color={poll.status === 'OPEN' ? 'green' : 'gray'}>{poll.status}</Badge>
                                    <span className="text-xs text-gray-500">{new Date(poll.created_at).toLocaleString()}</span>
                                    <span className="text-xs text-gray-500 font-bold">{poll.total_votes} Stimmen</span>
                                </div>
                                <h3 className="text-lg font-bold text-white mb-3">{poll.question}</h3>
                                
                                <div className="space-y-3">
                                    {poll.options.map(opt => {
                                        const tot = Math.max(1, parseInt(poll.total_votes as any));
                                        const pct = Math.round((opt.votes / tot) * 100);
                                        return (
                                            <div key={opt.id} className="space-y-1">
                                                <div className="relative h-6 bg-gray-800 rounded overflow-hidden">
                                                    <div style={{width: `${pct}%`}} className="h-full bg-blue-900/50"></div>
                                                    <div className="absolute inset-0 flex justify-between items-center px-2 text-xs">
                                                        <span className="text-white font-bold">{opt.label}</span>
                                                        <span className="text-gray-400">{pct}% ({opt.votes})</span>
                                                    </div>
                                                </div>
                                                {(user?.roles.includes(UserRole.WEBADMIN) || user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.STEWARD)) && opt.voters && opt.voters.length > 0 && (
                                                    <div className="text-[10px] text-gray-500 px-2 flex flex-wrap gap-1">
                                                        <span className="font-bold uppercase">Wähler:</span>
                                                        {opt.voters.join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            <div className="flex flex-col gap-2">
                                {poll.status === 'OPEN' ? (
                                    <Button size="sm" variant="secondary" onClick={() => handleStatus(poll.id, 'CLOSED')}>
                                        <StopCircle size={14} className="mr-2"/> Beenden
                                    </Button>
                                ) : (
                                    <Button size="sm" variant="secondary" onClick={() => handleStatus(poll.id, 'OPEN')}>
                                        <Play size={14} className="mr-2"/> Wieder öffnen
                                    </Button>
                                )}
                                <Button size="sm" variant="danger" onClick={() => handleDelete(poll.id)}>
                                    <Trash2 size={14}/>
                                </Button>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};
